<template>
  <div class="grading-tab">
    <div class="tab-header">
      <h4>그레이딩</h4>
      <el-button v-if="canEdit" type="primary" size="small" @click="openForm()">
        그레이딩 요청
      </el-button>
    </div>

    <!-- 완성치수 (그레이딩 결과, 2026-08-01 사이먼) — 원본을 여기(MR)서 입력.
         생산지시서 생성 시 규격으로 자동 승계 (수량·자재와 동일 패턴).
         기존의 "WI 규격에서 직접 입력"은 결과 문서에서 원본을 만드는 역순이라 정정 -->
    <div class="spec-section">
      <div class="spec-header">
        <h4>완성치수 (그레이딩 결과)</h4>
        <div v-if="canEdit">
          <el-button size="small" @click="addSpecRow">부위 추가</el-button>
          <el-button type="primary" size="small" :loading="specSaving" @click="saveSpecs">완성치수 저장</el-button>
        </div>
      </div>
      <div class="spec-hint">
        저장하면 <b>생산지시서 생성 시 규격으로 자동 승계</b>되어 PDF 완성치수 표로 인쇄됩니다.
        이미 만든 생산지시서에는 생산지시서 탭의 [동기화]로 반영하세요.
      </div>

      <el-alert v-if="specSizes.length === 0" type="info" :closable="false" show-icon
        title="수량배분 탭에서 사이즈를 먼저 구성하세요 — 그 사이즈가 완성치수의 열이 됩니다." />

      <div v-else class="spec-matrix-wrapper">
        <table class="spec-table">
          <thead>
            <tr>
              <th class="part-col">부위</th>
              <th v-for="sz in specSizes" :key="sz">{{ sz }}</th>
              <th>FPP</th>
              <th>패턴</th>
              <th>편차</th>
              <th v-if="canEdit" class="del-col"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, ri) in specRows" :key="ri">
              <td class="part-col">
                <el-input v-if="canEdit" v-model="row.part" size="small" placeholder="예: 어깨" />
                <span v-else>{{ row.part }}</span>
              </td>
              <td v-for="sz in specSizes" :key="sz">
                <el-input v-if="canEdit" v-model="row.cells[sz]" size="small" class="spec-cell" />
                <span v-else>{{ row.cells[sz] || '-' }}</span>
              </td>
              <td>
                <el-input v-if="canEdit" v-model="row.fpp" size="small" class="spec-cell" />
                <span v-else>{{ row.fpp || '-' }}</span>
              </td>
              <td>
                <el-input v-if="canEdit" v-model="row.pattern" size="small" class="spec-cell" />
                <span v-else>{{ row.pattern || '-' }}</span>
              </td>
              <td>
                <el-input v-if="canEdit" v-model="row.tolerance" size="small" class="spec-cell" />
                <span v-else>{{ row.tolerance || '-' }}</span>
              </td>
              <td v-if="canEdit" class="del-col">
                <el-button type="danger" link size="small" @click="specRows.splice(ri, 1)">×</el-button>
              </td>
            </tr>
            <tr v-if="specRows.length === 0">
              <td :colspan="specSizes.length + (canEdit ? 5 : 4)" class="empty-row">
                [부위 추가]로 어깨·상동·허리 등 부위를 추가하고 사이즈별 치수를 입력하세요.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 타임라인 형식 리스트 -->
    <el-timeline v-if="gradings.length > 0" :reverse="false">
      <el-timeline-item
        v-for="item in gradings"
        :key="item.id"
        :timestamp="formatDateTime(item.requested_at)"
        placement="top"
      >
        <div class="timeline-content">
          <div class="timeline-header">
            <div class="timeline-status">
              <el-tag
                :type="item.is_completed ? 'success' : 'info'"
                size="small"
              >
                {{ item.is_completed ? '완료' : '진행중' }}
              </el-tag>
              <span v-if="item.is_completed" class="completed-date">
                완료일: {{ formatDate(item.completed_at) }}
              </span>
            </div>
            <div v-if="canEdit && !item.is_completed" class="timeline-actions">
              <el-button
                type="primary"
                link
                size="small"
                @click="markComplete(item)"
              >
                완료 처리
              </el-button>
            </div>
          </div>

          <div class="timeline-details">
            <p v-if="item.supplier_name || item.due_date || item.estimated_cost" class="detail-row">
              <span class="label">발주 정보:</span>
              <span class="value">
                {{ item.supplier_name || '사내' }}
                <template v-if="item.due_date"> · 납기 {{ item.due_date }}</template>
                <template v-if="item.estimated_cost"> · 예상 {{ Number(item.estimated_cost).toLocaleString() }}원</template>
              </span>
            </p>
            <p v-if="item.memo" class="detail-row">
              <span class="label">메모:</span>
              <span class="value">{{ item.memo }}</span>
            </p>

            <div v-if="item.grading_file" class="detail-row">
              <span class="label">그레이딩 파일:</span>
              <el-link
                :href="item.grading_file"
                target="_blank"
                type="primary"
                :underline="false"
              >
                <el-icon><Document /></el-icon>
                파일 다운로드
              </el-link>
            </div>
          </div>
        </div>
      </el-timeline-item>
    </el-timeline>

    <el-empty v-else-if="!loading" description="등록된 그레이딩 요청이 없습니다." />

    <div v-if="loading" class="loading-container">
      <el-skeleton :rows="3" animated />
    </div>

    <!-- 그레이딩 요청 폼 다이얼로그 -->
    <el-dialog
      v-model="formVisible"
      title="그레이딩 요청"
      width="600px"
    >
      <el-form :model="form" label-width="80px" :rules="formRules" ref="formRef">
        <el-form-item label="외주업체">
          <el-select v-model="form.supplier" clearable filterable placeholder="사내 진행 시 비움" style="width: 100%">
            <el-option v-for="sp in suppliers" :key="sp.supplier_id" :label="sp.name" :value="sp.supplier_id" />
          </el-select>
        </el-form-item>
        <el-form-item label="납기일">
          <el-date-picker v-model="form.due_date" type="date" value-format="YYYY-MM-DD" style="width: 100%" />
        </el-form-item>
        <el-form-item label="예상비용">
          <el-input-number v-model="form.estimated_cost" :min="0" :step="10000" style="width: 200px" />
          <span class="cost-hint">원 — 참고용 (실지출은 지출결의로 집계)</span>
        </el-form-item>
        <el-form-item label="메모" prop="memo">
          <el-input
            v-model="form.memo"
            type="textarea"
            :rows="4"
            placeholder="그레이딩 요청 사항을 입력하세요"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="formVisible = false">취소</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="saving">
          요청
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { Document } from '@element-plus/icons-vue'
import axiosInstance from '@/api/axios'
import { ElMessage, ElMessageBox } from 'element-plus'

const props = defineProps({
  mainRequestId: { type: [Number, String], required: true },
  canEdit: { type: Boolean, default: false },
})

const loading = ref(false)
const saving = ref(false)
const gradings = ref([])

// ── 완성치수 (그레이딩 결과) — MR 원본 매트릭스 (2026-08-01) ──
const specSizes = ref([])   // 열 = 수량배분의 사이즈
const specRows = ref([])    // [{ part, part_en, fpp, pattern, tolerance, cells: {size: 치수} }]
const specSaving = ref(false)

const fetchSpecs = async () => {
  if (!props.mainRequestId) return
  try {
    // 열(사이즈)은 수량배분에서 — 요척→수량배분→그레이딩 업무 순서와 일치
    const qtyRes = await axiosInstance.get(
      `production/main-requests/${props.mainRequestId}/size-quantities/`)
    specSizes.value = qtyRes.data.sizes || []

    const res = await axiosInstance.get(
      `production/main-requests/${props.mainRequestId}/specs/`)
    const items = res.data.items || []
    const rows = []
    const byPart = {}
    for (const it of items) {
      if (!byPart[it.part]) {
        byPart[it.part] = { part: it.part, part_en: it.part_en || '',
          fpp: it.fpp || '', pattern: it.pattern_measurement || '',
          tolerance: it.tolerance || '', cells: {} }
        rows.push(byPart[it.part])
      }
      byPart[it.part].cells[it.size] = it.measurement
      if (!specSizes.value.includes(it.size)) specSizes.value.push(it.size)
    }
    specRows.value = rows
  } catch (e) { console.error('완성치수 조회 실패:', e) }
}

const addSpecRow = () => {
  specRows.value.push({ part: '', part_en: '', fpp: '', pattern: '', tolerance: '', cells: {} })
}

const saveSpecs = async () => {
  const items = []
  for (const row of specRows.value) {
    const part = (row.part || '').trim()
    if (!part) continue
    for (const sz of specSizes.value) {
      const m = (row.cells[sz] || '').toString().trim()
      if (!m) continue
      items.push({ part, part_en: row.part_en || '', size: sz, measurement: m,
        pattern_measurement: row.pattern || '', tolerance: row.tolerance || '',
        fpp: row.fpp || '' })
    }
  }
  specSaving.value = true
  try {
    await axiosInstance.post(
      `production/main-requests/${props.mainRequestId}/specs/`, { items })
    ElMessage.success(`완성치수 ${items.length}건 저장 — 생산지시서 생성 시 자동 승계됩니다.`)
    await fetchSpecs()
  } catch (e) {
    ElMessage.error('완성치수 저장 실패: ' + (e.response?.data?.detail || '오류 발생'))
  } finally {
    specSaving.value = false
  }
}
const formVisible = ref(false)
const formRef = ref(null)

const form = ref({
  memo: '', supplier: null, due_date: null, estimated_cost: null,
})

const suppliers = ref([])
const fetchSuppliers = async () => {
  try {
    const res = await axiosInstance.get('/suppliers/')
    suppliers.value = res.data.results || res.data || []
  } catch (e) {
    console.error('업체 목록 조회 실패:', e)
  }
}

const formRules = {
  memo: [{ required: false }],
}

const fetchGradings = async () => {
  if (!props.mainRequestId) return
  loading.value = true
  try {
    const res = await axiosInstance.get(
      `production/gradings/`,
      { params: { main_request: props.mainRequestId } }
    )
    gradings.value = Array.isArray(res.data) ? res.data : res.data.results || []
  } catch (e) {
    console.error('그레이딩 조회 실패:', e)
    ElMessage.error('그레이딩 조회 실패')
  } finally {
    loading.value = false
  }
}

const openForm = () => {
  form.value = { memo: '', supplier: null, due_date: null, estimated_cost: null }
  if (!suppliers.value.length) fetchSuppliers()
  formVisible.value = true
}

const formatDateTime = (val) => {
  if (!val) return ''
  const date = new Date(val)
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatDate = (val) => {
  if (!val) return ''
  const date = new Date(val)
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
}

const handleSubmit = async () => {
  if (formRef.value) {
    try {
      await formRef.value.validate()
    } catch {
      return
    }
  }
  saving.value = true
  try {
    const data = {
      main_request: props.mainRequestId,
      memo: form.value.memo,
      supplier: form.value.supplier || null,
      due_date: form.value.due_date || null,
      estimated_cost: form.value.estimated_cost || null,
    }
    await axiosInstance.post('production/gradings/', data)
    ElMessage.success('그레이딩이 요청되었습니다.')
    formVisible.value = false
    await fetchGradings()
  } catch (e) {
    ElMessage.error('요청 실패: ' + (e.response?.data?.detail || '오류 발생'))
  } finally {
    saving.value = false
  }
}

const markComplete = async (item) => {
  try {
    await ElMessageBox.confirm(
      '이 그레이딩을 완료 처리하시겠습니까?',
      '완료 확인',
      {
        confirmButtonText: '완료',
        cancelButtonText: '취소',
        type: 'warning',
      }
    )
    await axiosInstance.patch(`production/gradings/${item.id}/`, {
      is_completed: true,
    })
    ElMessage.success('완료 처리되었습니다.')
    await fetchGradings()
  } catch (e) {
    if (e.response) {
      ElMessage.error('처리 실패: ' + (e.response?.data?.detail || '오류 발생'))
    }
  }
}

watch(() => props.mainRequestId, () => { fetchGradings(); fetchSpecs() }, { immediate: true })

// 부모(메인오더 상세)가 탭 전환 시 호출 — 수량배분에서 사이즈 추가 직후에도
// 완성치수 열이 즉시 반영되도록 (2026-08-01, 요척 탭과 동일 패턴)
defineExpose({ refreshSpecs: fetchSpecs })

onMounted(() => {
  fetchGradings()
  fetchSpecs()
})
</script>

<style scoped>
.grading-tab {
  padding: 8px 0;
}

.spec-section { margin-bottom: 16px; }
.spec-header { display: flex; justify-content: space-between; align-items: center; }
.spec-header h4 { margin: 0; }
.spec-hint { font-size: 12px; color: #909399; margin: 4px 0 8px; }
.spec-matrix-wrapper { overflow-x: auto; }
.spec-table { border-collapse: collapse; width: 100%; }
.spec-table th, .spec-table td { border: 1px solid #ebeef5; padding: 3px 6px; text-align: center; font-size: 13px; }
.spec-table th { background: #f5f7fa; color: #606266; }
.spec-table .part-col { min-width: 90px; }
.spec-table .del-col { width: 36px; }
.spec-cell { width: 100%; }
.empty-row { color: #909399; padding: 12px; }

.tab-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.tab-header h4 {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
}

.timeline-content {
  background: #ffffff;
  border-radius: 4px;
  padding: 12px;
  margin-left: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
}

.timeline-content:hover {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
}

.timeline-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.timeline-status {
  display: flex;
  align-items: center;
  gap: 12px;
}

.completed-date {
  font-size: 12px;
  color: #909399;
  font-style: italic;
}

.timeline-actions {
  display: flex;
  gap: 8px;
}

.timeline-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-row {
  display: flex;
  gap: 8px;
  margin: 0;
  font-size: 13px;
  align-items: flex-start;
}

.detail-row .label {
  font-weight: 500;
  color: #606266;
  white-space: nowrap;
  min-width: 80px;
}

.detail-row .value {
  color: #303133;
  word-break: break-word;
  flex: 1;
  line-height: 1.5;
}

.loading-container {
  padding: 20px 0;
}

.cost-hint {
  margin-left: 8px;
  font-size: 12px;
  color: #909399;
}

:deep(.el-timeline-item__tail) {
  border-left: 2px solid #E4E7ED;
}

:deep(.el-timeline-item:last-child .el-timeline-item__tail) {
  border-left: 2px dashed #E4E7ED;
}

:deep(.el-timeline-item__node) {
  width: 12px;
  height: 12px;
  background-color: #409EFF;
  margin-left: -6px;
}

:deep(.el-timeline-item.is-last .el-timeline-item__node) {
  background-color: #67C23A;
}
</style>
